import { useMemo } from "react";
import moment from "moment";
import { Download, MapPin } from "lucide-react";
import { CardHeaderTag } from "components/shared/Tags/StatusTags";
import getHexColor from "utils/getHexColor";
import { DD_PO_PAGES } from "./config";
import {
  CellText,
  DeliveryAtCell,
  DownloadButton,
  PoIdCell,
  PoIdLink,
  RowActions,
  ViewDetailsLink,
} from "./styles.allDDPoView";

const formatDate = (date) => {
  if (!date || !moment(date).isValid()) {
    return "—";
  }
  return moment(date).format("DD MMM, YYYY");
};

const formatAmount = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`;

export default function useDDPoColumns(activePage, { handleDownloadInvoice, handleViewDetails, handleViewPayment }) {
  return useMemo(() => {
    const poIdColumn = {
      id: "po_id",
      header: "PO ID",
      meta: { width: 200 },
      cell: (info) => {
        const row = info.row.original;
        return (
          <PoIdCell>
            <PoIdLink type="button">{row.poNumber}</PoIdLink>
            {row.isMandi && (
              <CardHeaderTag
                text="MANDI"
                bgColor={getHexColor({ type: "grey", tint: 100 })}
                textColor={getHexColor({ type: "grey", tint: 600 })}
                allSidesRounded
              />
            )}
          </PoIdCell>
        );
      },
    };

    if (activePage === DD_PO_PAGES.DELIVERED) {
      return [
        poIdColumn,
        {
          id: "invoice_amount",
          header: "Invoice amount",
          meta: { width: 140 },
          cell: (info) => <CellText>{formatAmount(info.row.original.invoiceAmount)}</CellText>,
        },
        {
          id: "payment_status",
          header: "Payment status",
          meta: { width: 220 },
          cell: (info) => {
            const row = info.row.original;
            return row.isPaid ? (
              <CardHeaderTag
                text={`Paid on ${formatDate(row.paymentDate)}`}
                bgColor={getHexColor({ type: "green", tint: 100 })}
                textColor={getHexColor({ type: "green", tint: 600 })}
                allSidesRounded
              />
            ) : (
              <CardHeaderTag
                text="Payment processing"
                bgColor={getHexColor({ type: "orange", tint: 100 })}
                textColor={getHexColor({ type: "orange", tint: 600 })}
                allSidesRounded
              />
            );
          },
        },
        {
          id: "delivered_on",
          header: "Delivered on",
          meta: { width: 150 },
          cell: (info) => <CellText>{formatDate(info.row.original.deliveredOn)}</CellText>,
        },
        {
          id: "action",
          header: "Action",
          meta: { width: 160, sticky: "right" },
          cell: (info) => {
            const row = info.row.original;
            return (
              <RowActions>
                <DownloadButton
                  type="button"
                  $hasDoc={row.hasInvoice}
                  onClick={(e) => { e.stopPropagation(); row.hasInvoice && handleDownloadInvoice(row._raw); }}
                >
                  <Download size={16} color={row.hasInvoice ? "#E23744" : "#BDBDBD"} />
                </DownloadButton>
                {row.isPaid && (
                  <ViewDetailsLink type="button" onClick={(e) => { e.stopPropagation(); handleViewPayment(row._raw); }}>
                    View payment
                  </ViewDetailsLink>
                )}
              </RowActions>
            );
          },
        },
      ];
    }

    return [
      {
        id: "po_id",
        header: "PO ID",
        meta: { width: 200 },
        cell: (info) => {
          const row = info.row.original;
          const viewSource = activePage === DD_PO_PAGES.PENDING_INVOICE ? "pod" : "invoice";
          return (
            <PoIdCell>
              <PoIdLink type="button" $clickable>
                {row.poNumber}
              </PoIdLink>
              {row.isMandi && (
                <CardHeaderTag
                  text="MANDI"
                  bgColor={getHexColor({ type: "grey", tint: 100 })}
                  textColor={getHexColor({ type: "grey", tint: 600 })}
                  allSidesRounded
                />
              )}
            </PoIdCell>
          );
        },
      },
      {
        id: "skus",
        header: "SKUs",
        meta: { width: 100 },
        cell: (info) => <CellText>{`${info.row.original.skuCount} SKUs`}</CellText>,
      },
      {
        id: "net_weight",
        header: "Net weight",
        meta: { width: 120 },
        cell: (info) => <CellText>{info.row.original.netWeight}</CellText>,
      },
      {
        id: "deliver_by",
        header: "Deliver by",
        meta: { width: 150 },
        cell: (info) => <CellText>{formatDate(info.row.original.deliverBy)}</CellText>,
      },
      {
        id: "po_amount",
        header: "PO amount",
        meta: { width: 130 },
        cell: (info) => <CellText>{formatAmount(info.row.original.poAmount)}</CellText>,
      },
      {
        id: "delivery_at",
        header: "Delivery at",
        meta: { width: 200 },
        cell: (info) =>
          info.row.original.deliveryAt ? (
            <DeliveryAtCell>
              <MapPin size={16} />
              <CellText>{info.row.original.deliveryAt}</CellText>
            </DeliveryAtCell>
          ) : (
            <CellText>—</CellText>
          ),
      },
      {
        id: "action",
        header: "Action",
        meta: { width: 160, sticky: "right" },
        cell: (info) => {
          const row = info.row.original;
          return (
            <RowActions>
              <DownloadButton
                type="button"
                $hasDoc={row.hasInvoice}
                onClick={(e) => { e.stopPropagation(); row.hasInvoice && handleDownloadInvoice(row._raw); }}
              >
                <Download size={16} color={row.hasInvoice ? "#E23744" : "#BDBDBD"} />
              </DownloadButton>
            </RowActions>
          );
        },
      },
    ];
  }, [activePage, handleDownloadInvoice, handleViewDetails, handleViewPayment]);
}
